import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface TeacherStats {
  teacher_code: string;
  full_name: string | null;
  nickname: string | null;
  tier: string | null;
  total_students: number | null;
  total_hours: number | null;
  last_class_date: string | null;
  depleted_students: number | null;
  nearly_depleted_students: number | null;
  not_renewed_students: number | null;
  renewed_students: number | null;
}

export type SortField = 'teacher_code' | 'nickname' | 'total_students' | 'total_hours' | 'last_class_date' | 'depleted_students' | 'renewed_students';
export type SortOrder = 'asc' | 'desc';

export interface UseTeacherStatsOptions {
  sortField: SortField;
  sortOrder: SortOrder;
  search: string;
  tiers?: string[];
  teacherCodes?: string[];
  startDate?: string;
  endDate?: string;
}

export function useTeacherStats(options: UseTeacherStatsOptions) {
  const { sortField, sortOrder, search, tiers, teacherCodes, startDate, endDate } = options;
  const hasDateFilter = !!(startDate && endDate);

  return useQuery({
    queryKey: ['teacher-stats', sortField, sortOrder, search, tiers, teacherCodes, startDate, endDate],
    queryFn: async () => {
      if (hasDateFilter) {
        // Use Edge Function for date-filtered queries with depletion data
        const { data, error } = await supabase.functions.invoke(
          'get-teacher-stats-with-depletion',
          {
            body: { 
              startDate: startDate || null, 
              endDate: endDate || null 
            }
          }
        );

        if (error) throw error;

        let filteredData = (data?.teachers || []) as TeacherStats[];

        // Client-side tier filter
        if (tiers && tiers.length > 0) {
          filteredData = filteredData.filter(teacher => {
            if (tiers.includes('CUSTOM')) {
              return tiers.includes(teacher.tier || '') || teacher.tier?.startsWith('CUSTOM');
            }
            return tiers.includes(teacher.tier || '');
          });
        }

        // Client-side teacher filter
        if (teacherCodes && teacherCodes.length > 0) {
          filteredData = filteredData.filter(teacher => 
            teacherCodes.includes(teacher.teacher_code)
          );
        }

        // Client-side search
        if (search.trim()) {
          const searchTerm = search.trim().toLowerCase();
          filteredData = filteredData.filter(teacher => 
            teacher.teacher_code?.toLowerCase().includes(searchTerm) ||
            teacher.full_name?.toLowerCase().includes(searchTerm) ||
            teacher.nickname?.toLowerCase().includes(searchTerm)
          );
        }

        // Client-side sort
        filteredData.sort((a, b) => {
          let aVal = a[sortField];
          let bVal = b[sortField];

          // Handle nulls
          if (aVal === null || aVal === undefined) return 1;
          if (bVal === null || bVal === undefined) return -1;

          // Compare
          if (typeof aVal === 'string' && typeof bVal === 'string') {
            return sortOrder === 'asc' 
              ? aVal.localeCompare(bVal) 
              : bVal.localeCompare(aVal);
          }

          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
          }

          return 0;
        });

        return {
          data: filteredData,
          totalCount: filteredData.length,
        };
      } else {
        // Default: use view for all-time stats (no depletion data)
        let query = supabase
          .from('v_teacher_stats')
          .select('*', { count: 'exact' });

        // Search by teacher_code, full_name, or nickname
        if (search.trim()) {
          const searchTerm = search.trim();
          query = query.or(
            `teacher_code.ilike.%${searchTerm}%,full_name.ilike.%${searchTerm}%,nickname.ilike.%${searchTerm}%`
          );
        }

        // Filter by tier
        if (tiers && tiers.length > 0) {
          const customSelected = tiers.includes('CUSTOM');
          const otherTiers = tiers.filter(t => t !== 'CUSTOM');
          
          if (customSelected && otherTiers.length > 0) {
            query = query.or(`tier.in.(${otherTiers.join(',')}),tier.ilike.CUSTOM%`);
          } else if (customSelected) {
            query = query.ilike('tier', 'CUSTOM%');
          } else {
            query = query.in('tier', otherTiers);
          }
        }

        // Filter by teacher codes
        if (teacherCodes && teacherCodes.length > 0) {
          query = query.in('teacher_code', teacherCodes);
        }

        // Sorting - handle nulls
        query = query.order(sortField, { 
          ascending: sortOrder === 'asc',
          nullsFirst: false
        });

        const { data, error, count } = await query;

        if (error) throw error;

        // Add null values for depletion fields when using view
        const dataWithDepletion = (data || []).map(teacher => ({
          ...teacher,
          depleted_students: null,
          nearly_depleted_students: null,
          not_renewed_students: null,
          renewed_students: null,
        })) as TeacherStats[];

        return {
          data: dataWithDepletion,
          totalCount: count || 0,
        };
      }
    }
  });
}
